import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8090/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
  const token = localStorage.getItem(tokenKey);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Invoice {
  id: string;
  invoice_id: string;
  square_invoice_number?: string;
  stripe_customer_id: string;
  square_customer_id?: string;
  red_rock_id: string;
  status: string;
  total_amount: number;
  amount_paid: number;
  currency: string;
  payment_status: string;
  hosted_invoice_url: string;
  square_public_url?: string;
  invoice_pdf: string;
  due_date: string;
  period_start: string;
  period_end: string;
  memo: string;
  source: string;
  last_event_id: string;
  is_synced: boolean;
  created: string;
  updated: string;
  line_item_description?: any; // JSON field containing Square order line items
}

export interface InvoiceListResponse {
  page: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
  items: Invoice[];
}

class InvoiceService {
  async getInvoicesByCustomer(customerId: string, userRole: string = 'client', userId: string = ''): Promise<Invoice[]> {
    try {
      const response = await api.get(`/client-customer-invoice-view-access?id=${customerId}&role=${userRole}&userId=${userId}`);
      return response.data.items || [];
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Invoices not found');
      }
      if (error.response?.status === 403) {
        throw new Error('Access denied - you don\'t have permission to view these invoices');
      }
      throw new Error('Failed to fetch invoices');
    }
  }

  async getInvoice(invoiceId: string): Promise<Invoice> {
    try {
      const response = await api.get(`/collections/invoices/records/${invoiceId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Invoice not found');
      }
      throw new Error('Failed to fetch invoice');
    }
  }

  async getOpenInvoices(customerId: string): Promise<Invoice[]> {
    try {
      const response = await api.get(`/collections/invoices/records?filter=red_rock_id="${customerId}" && status="open"&sort=-created`);
      return response.data.items || [];
    } catch (error: any) {
      throw new Error('Failed to fetch open invoices');
    }
  }

  async getPaidInvoices(customerId: string): Promise<Invoice[]> {
    try {
      const response = await api.get(`/collections/invoices/records?filter=red_rock_id="${customerId}" && status="paid"&sort=-created`);
      return response.data.items || [];
    } catch (error: any) {
      throw new Error('Failed to fetch paid invoices');
    }
  }

  async getInvoiceByStripeId(stripeInvoiceId: string): Promise<Invoice | null> {
    try {
      const response = await api.get(`/collections/invoices/records?filter=invoice_id="${stripeInvoiceId}"`);
      const items = response.data.items || [];
      return items.length > 0 ? items[0] : null;
    } catch (error: any) {
      throw new Error('Failed to fetch invoice by Stripe ID');
    }
  }

  async updateSquareLinkStatus(redRockId: string): Promise<{
    customer_id: string;
    total_scheduled: number;
    processed: number;
    updated: number;
    failed: number;
    results: Array<{
      invoice_id: string;
      square_invoice_id: string | null;
      status: string;
      error?: string;
      square_public_url?: string;
      new_status?: string;
    }>;
    timestamp: string;
  }> {
    try {
      const response = await api.post('/update-square-link-status', {
        red_rock_id: redRockId,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating Square link status:', error);
      // Don't throw error - this is a background operation
      // Return a default response so the UI doesn't break
      return {
        customer_id: redRockId,
        total_scheduled: 0,
        processed: 0,
        updated: 0,
        failed: 0,
        results: [],
        timestamp: new Date().toISOString(),
      };
    }
  }
}

export const invoiceService = new InvoiceService();
